import { useState } from 'react';
import { get } from 'lodash';
import {
  Button,
  ENotificationType,
  Form,
  FormItem,
  Modal,
  useForm,
  useShowToastNotification,
  useTranslation,
  filterLabelOption,
} from '@packages/utils';
import { TValidateFieldsError } from 'data/types/generic.types';
import { EIconNames, Icon } from 'components/Base/Icon';
import Checkbox from 'components/Base/Checkbox';
import { Select } from 'components/Base/Select';
import { TUserIdentityUI } from 'data/types/user.types';
import {
  useAddContactMutation,
  useGetContactsIdentityQuery,
} from 'redux/apiSlice/elcs.slice';
import { TAddContactFormProps, TAddContactProps } from './AddContact.types';
import useGetFormData from './AddContact.data';

const AddContact = ({ elcId }: TAddContactProps) => {
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.elcs.detail.contacts-tab.list.actions.add-contact',
  });
  const { addContactFormFields } = useGetFormData();
  const { isElacMember, userId } = addContactFormFields;
  const { showToastNotification } = useShowToastNotification();
  const { form } = useForm<TAddContactFormProps>();
  const [open, setOpen] = useState<boolean>(false);
  const [addContact, { isLoading }] = useAddContactMutation();

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const { data: people } = useGetContactsIdentityQuery({
    elcId,
  });

  const peopleOptions = people?.map((item: TUserIdentityUI) => ({
    label: item.fullName,
    value: item.id,
  }));

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await addContact({
        elcId,
        ...values,
      }).unwrap();
      showToastNotification({
        message: t('modal.SUCCESS_MESSAGE'),
        type: ENotificationType.SUCCESS,
      });
      handleClose();
    } catch (err) {
      if (!(err as TValidateFieldsError)?.errorFields) {
        const message = get(err, 'data.message');
        showToastNotification({
          message,
          type: ENotificationType.ERROR,
        });
      }
    }
  };

  return (
    <>
      <Button
        color="primary"
        type="primary"
        onClick={() => setOpen(true)}
        icon={<Icon icon={EIconNames.PLUS} className="pointer" />}
      >
        {t('ADD_CONTACT_BTN')}
      </Button>
      <Modal
        title={t('modal.TITLE')}
        open={open}
        onCancel={handleClose}
        onOk={handleSubmit}
        centeredPosition
        okText={t('modal.SUBMIT_BTN')}
        cancelText={t('modal.CANCEL_BTN')}
        confirmLoading={isLoading}
      >
        <Form form={form} initialValues={{ isElacMember: false }}>
          <FormItem {...userId} valuePropName="checked">
            <Select
              filterOption={filterLabelOption}
              showSearch
              placeholder={userId.placeholder}
              options={peopleOptions}
            />
          </FormItem>
          <FormItem name={isElacMember.name} valuePropName="checked">
            <Checkbox>{isElacMember.label}</Checkbox>
          </FormItem>
        </Form>
      </Modal>
    </>
  );
};

export default AddContact;
